import {action, IReactionDisposer, reaction} from 'mobx';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {GridBasis} from '@mintdata-wsp/common/state/ui/visualDesigner/grid/GridBasis';
import {IMutableGridModel} from '@mintdata-wsp/common/state/ui/visualDesigner/grid/GridModel';
import {WSPIconName, WSPIcons, WSPIconSize} from '@mintdata-wsp/resources/icons/WSPIcons';
import {IconCell} from '@mintdata-wsp/wsp/ui/components/grid/cells/IconCell';
import {LetterCell} from '@mintdata-wsp/wsp/ui/components/grid/cells/LetterCell';
import {TextCell} from '@mintdata-wsp/wsp/ui/components/grid/cells/TextCell';
import {TimestampCell} from '@mintdata-wsp/wsp/ui/components/grid/cells/TimestampCell';
import {GridListColumn, GridModels} from '@mintdata-wsp/wsp/ui/components/grid/GridModels';
import {DateTimeUtil} from '@mintdata-wsp/wsp/util/DateTimeUtil';
import {Flow, MessageSeverity, MessageSource} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';

export class ConsolePanelController
{
    private _disposers:IReactionDisposer[] = [];

    public init()
    {
        this._setupGrid();
        this._listenForOpenFlow();
        this._listenForFlowSimulationMessages();
    }

    public getGridModel():IMutableGridModel
    {
        return this._getGridModel();
    }

    public clearAllMessages():void
    {
        const gridModel = this.getGridModel();
        gridModel.clearDataRows();
    }

    @action
    private _setupGrid()
    {
        const columns = [
            new GridListColumn('logLevel', '', GridBasis.fixed(25)),
            new GridListColumn('message', 'Message', GridBasis.stretch(1)),
            new GridListColumn('timestamp', 'Timestamp', GridBasis.fixed(220)),
            new GridListColumn('source', 'Source', GridBasis.fixed(20))
        ];

        const gridModel = GridModels.newForList(columns, false, true, false);

        this._setGridModel(gridModel);
    }

    private _listenForOpenFlow()
    {
        const disposer = reaction(
            () => controllers.ui.flow.getOpenFlow(),
            (flow:Flow) => this._onFlowChanged(flow)
        );

        this._disposers.push(disposer);
    }

    private _listenForFlowSimulationMessages()
    {
        const disposer = reaction(
            () =>
            {
                const openFlow = controllers.ui.flow.getOpenFlow();

                if( openFlow )
                {
                    const simulation = controllers.ui.flowSimulation.getForFlowEditor(openFlow.id);

                    if( simulation )
                    {
                        return simulation.messages.length;
                    }
                }

                return 0;

            },
            (messageCount) => this._onFlowSimulationMessagesChanged(messageCount)
        );

        this._disposers.push(disposer);
    }

    @action
    private _onFlowChanged(flow:Flow | null)
    {
        this.clearAllMessages();

        if( flow )
        {
            const simulation = controllers.ui.flowSimulation.getForFlowEditor(flow.id);
            const messages = simulation ? simulation.messages : [];

            for( const message of messages )
            {
                this._logMessage(message.message, message.severity, message.source);
            }
        }
    };

    @action
    private _onFlowSimulationMessagesChanged(messageCount:number):void
    {
        const flow = controllers.ui.flow.getOpenFlow();

        this._onFlowChanged(flow);
    };

    private _logMessage(message:any, severity:MessageSeverity, source:MessageSource):void
    {
        const timestamp = DateTimeUtil.getNow();
        const logLevelIcon = this._logLevelToIconName(severity);
        const letterCell = this._messageSourceToCell(source);

        this._appendRow(logLevelIcon, message, timestamp, letterCell);
    }

    private _messageSourceToCell(source:MessageSource):LetterCell
    {
        switch( source )
        {
            case MessageSource.UI:
                return new LetterCell('U', 'UI');
            case MessageSource.TopologyControl:
                return new LetterCell('T', 'Topology');
            case MessageSource.VisualData:
                return new LetterCell('V', 'Visual Data');
            case MessageSource.Network:
                return new LetterCell('N', 'Network');
            case MessageSource.Cloud:
                return new LetterCell('C', 'Cloud');
            default:
                return new LetterCell('S', 'System');
        }
    }

    private _appendRow(iconName:WSPIconName,
                       message:string,
                       timestamp:number,
                       letterCell:LetterCell)
    {
        const row =
            [
                new IconCell(iconName, WSPIconSize.medium16),
                new TextCell(message),
                new TimestampCell(timestamp),
                letterCell
            ];

        const gridModel = this._getGridModel();
        gridModel.appendDataRow(row);
    }

    private _logLevelToIconName(severity:MessageSeverity):WSPIconName | null
    {
        if( !severity )
            return null;

        if( severity === MessageSeverity.Info )
            return WSPIcons.visualDesigner.panels.console.Info;
        else if( severity === MessageSeverity.Warn )
            return WSPIcons.visualDesigner.panels.console.Warning;
        else if( severity === MessageSeverity.Error )
            return WSPIcons.visualDesigner.panels.console.Error;

        return null;
    }

    private _setGridModel(gridModel:IMutableGridModel):void
    {
        rootState.ui.visualDesigner.workbench.panels.console.consoleList = gridModel;
    }

    private _getGridModel():IMutableGridModel
    {
        return rootState.ui.visualDesigner.workbench.panels.console.consoleList;
    }
}
